import {
  CandlestickSeries,
  ColorType,
  createChart,
  createSeriesMarkers,
  CrosshairMode,
  HistogramSeries,
  LineStyle,
} from 'lightweight-charts';
import type {
  CandlestickData,
  HistogramData,
  ISeriesMarkersPluginApi,
  MouseEventParams,
  SeriesMarker,
  Time,
} from 'lightweight-charts';
import { useEffect, useMemo, useRef, useState } from 'react';

import type {
  PositionReplayBarDto,
  PositionReplayEpisodeDto,
  PositionReplayExecutionDto,
} from '../../lib/api/endpoints';
import { formatCurrency } from '../../lib/format/currency';
import { useUiStore } from '../../stores/uiStore';
import { Button } from '../ui/Button';

import { formatFlatDuration } from './ReplayExecutionList';

interface ReplayCandlestickChartProps {
  bars: readonly PositionReplayBarDto[];
  currency: string;
  episodes: readonly PositionReplayEpisodeDto[];
  executions: readonly PositionReplayExecutionDto[];
  symbol: string;
}

interface DividerPosition {
  id: number;
  label: string;
  x: number;
}

interface CrosshairReadout {
  close: number;
  date: string;
  executions: readonly PositionReplayExecutionDto[];
  high: number;
  low: number;
  open: number;
}

interface OhlcPoint {
  close: number;
  high: number;
  low: number;
  open: number;
}

const CHART_HEIGHT = 360;
const UP_COLOR = '#10B981';
const DOWN_COLOR = '#EF4444';
const APPROXIMATE_BUY_COLOR = '#6EE7B7';
const APPROXIMATE_SELL_COLOR = '#FCA5A5';
const MAX_BAR_SPACING = 12;
const MAX_MARKER_SIZE = 2.5;
const MARKER_BASELINE_SCALE = 0.75;

function displayTime(time: Time): string {
  if (typeof time === 'string') return time;
  if (typeof time === 'number') return new Date(time * 1_000).toISOString().slice(0, 10);
  return `${time.year}-${String(time.month).padStart(2, '0')}-${String(time.day).padStart(2, '0')}`;
}

function executionMarkers(executions: readonly PositionReplayExecutionDto[]): SeriesMarker<Time>[] {
  return [...executions]
    .sort((left, right) => left.date.localeCompare(right.date) || left.id.localeCompare(right.id))
    .map((execution) => {
      const isBuy = execution.side === 'BUY';
      const approximate = !execution.timeKnown;
      return {
        id: execution.id,
        time: execution.date,
        position: 'atPriceMiddle',
        price: Number(execution.price),
        shape: approximate ? 'square' : isBuy ? 'arrowUp' : 'arrowDown',
        color: approximate
          ? isBuy
            ? APPROXIMATE_BUY_COLOR
            : APPROXIMATE_SELL_COLOR
          : isBuy
            ? UP_COLOR
            : DOWN_COLOR,
        size: executionMarkerSize(execution.quantity),
      } satisfies SeriesMarker<Time>;
    });
}

function executionMarkerSize(quantity: string): number {
  const numericQuantity = Math.abs(Number(quantity));
  const quantityScale =
    !Number.isFinite(numericQuantity) || numericQuantity <= 1
      ? 1
      : Math.min(MAX_MARKER_SIZE, 1 + Math.log10(numericQuantity) * 0.5);
  return MARKER_BASELINE_SCALE * quantityScale;
}

function crosshairData(
  param: MouseEventParams<Time>,
  candleData: OhlcPoint | undefined,
  executionsByDate: ReadonlyMap<string, readonly PositionReplayExecutionDto[]>,
): CrosshairReadout | null {
  if (!param.time || !candleData) return null;
  const date = displayTime(param.time);
  return {
    date,
    executions: executionsByDate.get(date) ?? [],
    open: candleData.open,
    high: candleData.high,
    low: candleData.low,
    close: candleData.close,
  };
}

export function ReplayCandlestickChart({
  bars,
  currency,
  episodes,
  executions,
  symbol,
}: ReplayCandlestickChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const markerPluginRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  const resetViewRef = useRef<() => void>(() => undefined);
  const [crosshair, setCrosshair] = useState<CrosshairReadout | null>(null);
  const [dividerPositions, setDividerPositions] = useState<DividerPosition[]>([]);
  const executionMarkersVisible = useUiStore((state) => state.executionMarkersVisible);
  const setExecutionMarkersVisible = useUiStore((state) => state.setExecutionMarkersVisible);
  const executionMarkersVisibleRef = useRef(executionMarkersVisible);
  executionMarkersVisibleRef.current = executionMarkersVisible;
  const candleData = useMemo<CandlestickData<Time>[]>(
    () =>
      bars.map((bar) => ({
        time: bar.date,
        open: Number(bar.adjustedOpen),
        high: Number(bar.adjustedHigh),
        low: Number(bar.adjustedLow),
        close: Number(bar.adjustedClose),
      })),
    [bars],
  );
  const volumeData = useMemo<HistogramData<Time>[]>(
    () =>
      bars.map((bar) => ({
        time: bar.date,
        value: Number(bar.adjustedVolume),
        color:
          Number(bar.adjustedClose) >= Number(bar.adjustedOpen)
            ? 'rgba(16, 185, 129, 0.55)'
            : 'rgba(239, 68, 68, 0.55)',
      })),
    [bars],
  );
  const executionsByDate = useMemo(() => {
    const grouped = new Map<string, PositionReplayExecutionDto[]>();
    for (const execution of executions) {
      const existing = grouped.get(execution.date);
      if (existing) existing.push(execution);
      else grouped.set(execution.date, [execution]);
    }
    return grouped;
  }, [executions]);
  const markerData = useMemo(() => executionMarkers(executions), [executions]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const chart = createChart(container, {
      width: Math.max(container.clientWidth, 1),
      height: CHART_HEIGHT,
      layout: {
        background: { type: ColorType.Solid, color: '#11151D' },
        textColor: '#A1A8B8',
        fontFamily: 'Inter, system-ui, sans-serif',
        panes: {
          separatorColor: '#2A3142',
          separatorHoverColor: '#3A4256',
          enableResize: true,
        },
      },
      grid: {
        vertLines: { color: 'rgba(42, 49, 66, 0.65)', style: LineStyle.Dotted },
        horzLines: { color: 'rgba(42, 49, 66, 0.65)', style: LineStyle.Dotted },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          color: '#6B7280',
          labelBackgroundColor: '#222837',
          style: LineStyle.Dashed,
        },
        horzLine: {
          color: '#6B7280',
          labelBackgroundColor: '#222837',
          style: LineStyle.Dashed,
        },
      },
      handleScale: {
        axisDoubleClickReset: { price: true, time: true },
        axisPressedMouseMove: { price: true, time: true },
        mouseWheel: true,
        pinch: true,
      },
      handleScroll: {
        horzTouchDrag: true,
        mouseWheel: true,
        pressedMouseMove: true,
        vertTouchDrag: true,
      },
      rightPriceScale: { borderColor: '#2A3142' },
      timeScale: {
        borderColor: '#2A3142',
        maxBarSpacing: MAX_BAR_SPACING,
        timeVisible: false,
        secondsVisible: false,
      },
    });
    const candles = chart.addSeries(CandlestickSeries, {
      upColor: UP_COLOR,
      downColor: DOWN_COLOR,
      borderUpColor: '#34D399',
      borderDownColor: '#F87171',
      wickUpColor: '#6EE7B7',
      wickDownColor: '#FCA5A5',
      priceLineColor: '#3B82F6',
    });
    candles.setData(candleData);
    const markerPlugin = createSeriesMarkers(
      candles,
      executionMarkersVisibleRef.current ? markerData : [],
      { autoScale: true },
    );
    markerPluginRef.current = markerPlugin;

    const volume = chart.addSeries(
      HistogramSeries,
      {
        priceFormat: { type: 'volume' },
        priceLineVisible: false,
        lastValueVisible: false,
      },
      1,
    );
    volume.setData(volumeData);
    chart.panes()[0]?.setHeight(270);
    chart.panes()[1]?.setHeight(90);
    chart.timeScale().fitContent();
    resetViewRef.current = () => chart.timeScale().fitContent();

    const updateDividers = () => {
      const next = episodes.slice(1).flatMap((episode) => {
        const coordinate = chart.timeScale().timeToCoordinate(episode.startedAt);
        return coordinate === null
          ? []
          : [
              {
                id: episode.id,
                label: formatFlatDuration(episode.flatDaysBefore ?? 0),
                x: coordinate,
              },
            ];
      });
      setDividerPositions(next);
    };
    const handleCrosshairMove = (param: MouseEventParams<Time>) => {
      const point = param.seriesData.get(candles);
      setCrosshair(
        crosshairData(param, point && 'open' in point ? point : undefined, executionsByDate),
      );
    };
    const resize = () => {
      const bounds = container.getBoundingClientRect();
      chart.resize(Math.max(bounds.width, 1), CHART_HEIGHT);
      updateDividers();
    };
    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => resize());

    chart.subscribeCrosshairMove(handleCrosshairMove);
    chart.timeScale().subscribeVisibleTimeRangeChange(updateDividers);
    resizeObserver?.observe(container);
    window.addEventListener('resize', resize);
    updateDividers();

    return () => {
      chart.unsubscribeCrosshairMove(handleCrosshairMove);
      chart.timeScale().unsubscribeVisibleTimeRangeChange(updateDividers);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', resize);
      if (markerPluginRef.current === markerPlugin) markerPluginRef.current = null;
      resetViewRef.current = () => undefined;
      chart.remove();
    };
  }, [candleData, episodes, executionsByDate, markerData, volumeData]);

  useEffect(() => {
    markerPluginRef.current?.setMarkers(executionMarkersVisible ? markerData : []);
  }, [executionMarkersVisible, markerData]);

  return (
    <div className="space-y-2">
      <div
        className="relative h-[360px] w-full min-w-0 overflow-hidden rounded border border-border bg-bg-muted"
        data-testid="replay-candlestick-chart"
        role="img"
        aria-label={`${symbol} ${bars.length} adjusted daily OHLCV candles and trade executions`}
      >
        <div className="h-full w-full" ref={containerRef} />
        <div
          className="pointer-events-none absolute left-3 top-2 z-10 rounded bg-surface/90 px-2 py-1 font-mono text-xs text-text"
          data-testid="crosshair-readout"
        >
          {crosshair ? (
            <>
              <div>
                <span className="mr-2 text-text-muted">{crosshair.date}</span>O{' '}
                {formatCurrency(crosshair.open, currency)} H{' '}
                {formatCurrency(crosshair.high, currency)} L{' '}
                {formatCurrency(crosshair.low, currency)} C{' '}
                {formatCurrency(crosshair.close, currency)}
              </div>
              {crosshair.executions.map((execution) => (
                <div key={execution.id} className="mt-1">
                  <span className={execution.side === 'BUY' ? 'text-success' : 'text-danger'}>
                    {execution.timeKnown ? '' : '≈ '}
                    {execution.side}
                  </span>{' '}
                  {execution.quantity} @ {formatCurrency(execution.price, currency)} on{' '}
                  {execution.date}
                </div>
              ))}
            </>
          ) : (
            <span className="text-text-muted">Hover for date and OHLC</span>
          )}
        </div>
        {dividerPositions.map((divider) => (
          <div
            key={divider.id}
            className="pointer-events-none absolute inset-y-0 z-10 border-l border-dashed border-primary"
            data-episode-divider={divider.id}
            style={{ left: `${divider.x}px` }}
          >
            <span className="absolute left-1 top-9 whitespace-nowrap rounded bg-surface/90 px-1.5 py-1 text-xs font-medium text-primary">
              {divider.label}
            </span>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-text-muted">
        <div className="flex flex-wrap gap-3" aria-label="Execution marker legend">
          <span className="text-success">▲ Buy</span>
          <span className="text-danger">▼ Sell</span>
          <span className="text-text-muted">□ ≈ Approximate time</span>
          <span>Marker size = execution quantity</span>
        </div>
        <div className="flex items-center gap-3">
          <Button
            aria-label={
              executionMarkersVisible ? 'Hide execution markers' : 'Show execution markers'
            }
            size="sm"
            type="button"
            variant="secondary"
            onClick={() => setExecutionMarkersVisible(!executionMarkersVisible)}
          >
            {executionMarkersVisible ? 'Hide markers' : 'Show markers'}
          </Button>
          <Button
            aria-label="Reset replay chart view"
            size="sm"
            type="button"
            variant="secondary"
            onClick={() => resetViewRef.current()}
          >
            Reset view
          </Button>
          <span>
            Charts by{' '}
            <a
              className="text-primary hover:text-primary-hover"
              href="https://www.tradingview.com/lightweight-charts/"
              rel="noreferrer"
              target="_blank"
            >
              TradingView Lightweight Charts
            </a>
          </span>
        </div>
      </div>
    </div>
  );
}
